import {
  Listing,
  ListingColumn,
  NotificationRequest,
  NotificationType,
  optimizedRetryOption,
  useNotificationRequest,
} from "o4a-react";
import * as React from "react";
import { useBulkQuery } from "savant-connector";
import { Pivot, PivotItem, Stack } from "@fluentui/react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureVnetLink } from "../../components/AzureLinks/AzureVnetLink";
import { OciSubnetLink } from "../../components/OciLinks/OciSubnetLink";
import { DetailsPanelId, PageId } from "../../constants/pluginConstants";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { MultiCloudVirtualNetwork, NetworkLink } from "../../gen/clients/mchub-azure-api-client";
import { parseId } from "../../helpers/idHelper";
import { AzureSubscriptionSummaryExt, useSubscriptions } from "../../hooks/useSubscriptions";
import { getOciRegion } from "../../utils";

export enum PivotItemTestIds {
  OciSubnets = "OciSubnets",
  VirtualNetworks = "VirtualNetworks"
}

enum OciSubnetColumnIds {
  Id = "id",
}

enum NetworkColumnIds {
  Id = "id",
  NVAIpAddress = "nvaIpAddress",
  RouterIpAddresses = "routerIpAddresses",
}

interface VnetListItem {
  id: string | undefined,
  nvaIpAddress: string | undefined,
  routerIpAddresses: string[] | undefined,
}

export interface McvcnConfigurationDetailsProps {
  mcvcn: MultiCloudVirtualNetwork | undefined;
  location: string;
}

export const McvcnConfigurationDetails = ({ mcvcn, location }: McvcnConfigurationDetailsProps): JSX.Element => {
  const { subscriptionId, resourceGroup } = parseId(mcvcn?.id);
  const { loading: subscriptionLoading, subscriptions } = useSubscriptions();
  const subscription = !subscriptionLoading && subscriptions
    ? subscriptions.find(s => s.id === subscriptionId)
    : undefined;

  const ociSubnets = mcvcn?.ociSubnetOcids?.map(ocid => ({ id: ocid })) || [];
  const getNVAIpAddress = (
    value: VnetListItem,
  // eslint-disable-next-line react/jsx-no-useless-fragment
  ): JSX.Element => <>{(value?.nvaIpAddress)}</>;

  const getRouterIpAddress = (
    value: VnetListItem,
    // eslint-disable-next-line react/jsx-no-useless-fragment
  ): JSX.Element => <>{(value?.routerIpAddresses?.join(", ")) }</>;

  const ociSubnetcolumns: ListingColumn[] = [
    {
      itemProp: OciSubnetColumnIds.Id,
      name: Messages.labels.subnet(),
      flexGrow: 1,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <OciSubnetLink
          id={value.id}
          vcnId={mcvcn?.ociVcnOcid as string}
          subscription={subscription as AzureSubscriptionSummaryExt}
          location={mcvcn?.location as string || location}
          analytics={{ pageId: PageId.MCVCN_DETAILS, panelId: DetailsPanelId.CONFIGURATION }}
        />
      ),
    },
  ];
  const networkLinkIds = mcvcn?.networkLinkIds;
  const wait = !(mcvcn?.networkLinkIds);
  const args = wait ? [] : networkLinkIds?.map(network => ({
    networkLinkName: decodeURIComponent(parseId(network)?.resourceName || ""),
    subscriptionId: subscriptionId || "",
    resourceGroupName: resourceGroup,
    apiVersion: MultiCloudDatabaseApiVersion,
  })) || [];

  const { submit: submitNotificationRequest } = useNotificationRequest();
  const { aggregatedResults: { error, loading }, results } = useBulkQuery({
    wait,
    method: apiClients.withRegion(getOciRegion(location)).netLinkApi.getNetworkLink,
    options: {
      args,
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
  });
  React.useEffect(() => {
    if (!loading && error) {
      const apiErrorsIndexes : number[] = [];
      results.forEach((result, index) => {
        if (result?.error) {
          apiErrorsIndexes.push(index);
        }
      });
      apiErrorsIndexes.forEach(apiErrorsIndex => {
        submitNotificationRequest({
          type: NotificationType.FAILURE,
          title: Messages.notifications.failure.titles.load(),
          // eslint-disable-next-line max-len
          message: Messages.notifications.failure.messages.loadNetworkLinkWithName(args?.[apiErrorsIndex].networkLinkName),
          apiError: results[apiErrorsIndex].error?.body.message,
        } as NotificationRequest);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, loading]);

  // Do not check for error while displaying data since no results will show even if certain calls go through
  const data = !loading
    ? results.reduce<NetworkLink[]>((acc, result) => acc.concat(result?.response?.data || []), []) : [];

  const vnets = (): VnetListItem[] => {
    const vnet: VnetListItem[] = [];

    if (data && data?.length > 0) {
      data.forEach(item => {
        vnet.push(
          {
            id: item?.properties?.azureAttachedNetworkIds?.[0],
            nvaIpAddress: item?.properties?.routingOptions?.customerNvaIpAddress,
            routerIpAddresses: item?.properties?.routerIpAddresses,
          },
        );
      });
    }
    return vnet || [];
  };

  const networkcolumns: ListingColumn[] = [
    {
      itemProp: NetworkColumnIds.Id,
      name: Messages.labels.name(),
      flexGrow: 1,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <AzureVnetLink vnetId={value.id as string || ""} />
      ),
    },
    {
      itemProp: NetworkColumnIds.NVAIpAddress,
      name: Messages.labels.networkVirtualAppliance(),
      flexGrow: 1,
      onRenderItems: value => (
        getNVAIpAddress(value)
      ),

    },
    {
      itemProp: NetworkColumnIds.RouterIpAddresses,
      name: Messages.labels.routerIpAddress(),
      flexGrow: 1,
      onRenderItems: value => (
        getRouterIpAddress(value)
      ),
    },
  ];

  return (
    <Stack style={{ height: "100%" }}>
      <Pivot style={{ height: "100%" }} styles={{ itemContainer: { height: "calc(100% - 44px)" } }}>
        <PivotItem
          headerButtonProps={{ "data-test-id": PivotItemTestIds.VirtualNetworks }}
          headerText={Messages.labels.virtualNetworks()}
          style={{ height: "100%" }}
        >
          <Listing
            items={vnets()}
            itemKey={NetworkColumnIds.Id}
            listColumns={networkcolumns}
            emptyList={{ title: Messages.common.noResults() }}
          />

        </PivotItem>

        <PivotItem
          headerButtonProps={{ "data-test-id": PivotItemTestIds.OciSubnets }}
          headerText={Messages.detailMcvcn.configuration.sectionTitles.ociSubnets()}
          style={{ height: "100%" }}
        >
          <Listing
            itemKey={OciSubnetColumnIds.Id}
            items={ociSubnets}
            emptyList={{ title: Messages.common.noResults() }}
            listColumns={ociSubnetcolumns}
          />

        </PivotItem>

      </Pivot>
    </Stack>

  );
};
